import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-stone-50 noise-texture">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/landing"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-primary transition-colors mb-8"
          data-testid="back-home-link"
        >
          <ArrowLeft size={20} />
          Back to Home
        </Link>

        <h1 className="text-4xl font-serif font-semibold text-stone-800 mb-6" data-testid="privacy-title">
          Privacy Policy
        </h1>

        <div className="bg-white rounded-2xl p-8 shadow-sm space-y-6 text-stone-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">Your Data, Your Ownership</h2>
            <p>
              AraVela is built on the principle that your thoughts and reflections belong to you alone.
              We do not store your conversations or personal content on our servers. Your journal and
              session history live encrypted on your device.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">What We Store</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Nothing on our servers:</strong> AraVela does not store your conversations or personal content on any server.</li>
              <li><strong>Nothing is saved unless you choose to:</strong> Chat sessions live only in memory while you're using them. If you tap <em>Save to Diary</em>, that session is encrypted with a key derived from your passcode and stored locally in your browser (IndexedDB). If you don't save, the conversation is discarded when you close the session, log out, or navigate away.</li>
              <li><strong>Encryption:</strong> Saved data is encrypted using AES-GCM with a key derived from your passcode via PBKDF2. Without your passcode, saved data is unreadable — including to us.</li>
              <li><strong>No recovery:</strong> We cannot recover your data if you forget your passcode. This is by design.</li>
              <li><strong>Optional Auto Check-In Alerts:</strong> If you turn on Auto Check-In Alerts (off by default) in Safety, AraVela may retain a minimal local marker (timestamp and intensity only — never your messages) so Vela can offer a gentle follow-up. Turning the setting off clears any pending markers.</li>
              <li><strong>Trusted contacts:</strong> Any contacts you add for check-ins are also stored locally and encrypted.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">What Gets Sent to AI</h2>
            <p>
              When you chat with Vela, your message is transmitted to an AI provider (OpenAI, via a
              secure proxy) solely to generate Vela's response in real time. If you choose Save to
              Diary, a short summary is also generated the same way as part of that explicit save.
              Your messages are not stored on our servers and are not used to train AI models.
              Transmission for AI processing is temporary and distinct from persistent storage.
            </p>
            <p className="mt-2 text-sm text-stone-600">
              If you'd prefer not to share a specific reflection with an AI service, simply don't
              send it in chat — you can still keep a private, offline journal in any format you
              prefer.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">Check-In Notifications</h2>
            <p>
              If you add trusted contacts and send a check-in request, we send ONLY a generic message:
              "This is a check-in request. Someone you care about may need support right now."
            </p>
            <p className="mt-2">
              No chat content, diary entries, or personal details are included in these notifications.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">Analytics</h2>
            <p>
              We do not use analytics or tracking cookies. We do not collect usage data, IP addresses, or device information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">Your Rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your content is your intellectual property.</li>
              <li>You can export your data at any time from the Memory page.</li>
              <li>You can delete your data by clearing your browser's local storage.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-serif font-semibold text-stone-800 mb-3">Changes to This Policy</h2>
            <p>
              If we make changes to this privacy policy, we will update this page. Continued use of AraVela
              constitutes acceptance of any changes.
            </p>
          </section>

          <section className="pt-4 border-t border-stone-200">
            <p className="text-sm text-stone-500">
              Last updated: January 2026
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;